'use client'

import type { LinkProps } from '@pearlconvert/ui/components/link/link.types'

import NextLink from 'next/link'

import { LinkForesight } from '@pearlconvert/ui/components/link/link-foresight'
import { LinkHoverPrefetch } from '@pearlconvert/ui/components/link/link-hover-prefetch'
import { usePrefetchMode } from '@pearlconvert/ui/providers'

function isAbsoluteUrl(url: string): boolean {
  return new RegExp('^(//|[a-z]+:)', 'i').test(url)
}

export default function Link({
  href,
  target,
  children,
  className,
  scroll,
  prefetch,
  prefetchOnHover,
  prefetchForesight,
  hitSlop,
  onMouseEnter,
  ...props
}: LinkProps) {
  const prefetchMode = usePrefetchMode()

  // 1. Absolute URL → plain <a>
  if (isAbsoluteUrl(href as string)) {
    return (
      <a
        href={href as string}
        target={target}
        className={className}
        onMouseEnter={onMouseEnter}
        {...props}
      >
        {children}
      </a>
    )
  }

  // 2. Explicit prefetch prop → caller override
  if (prefetch !== undefined) {
    return (
      <NextLink
        className={className}
        target={target}
        href={href}
        scroll={scroll}
        prefetch={prefetch}
        onMouseEnter={onMouseEnter}
        {...props}
      >
        {children}
      </NextLink>
    )
  }

  // 3. Hover prefetch (per-link override or global mode)
  if (prefetchOnHover ?? prefetchMode === 'hover') {
    return (
      <LinkHoverPrefetch
        href={href}
        target={target}
        className={className}
        scroll={scroll}
        onMouseEnter={onMouseEnter}
        {...props}
      >
        {children}
      </LinkHoverPrefetch>
    )
  }

  // 4. Foresight prefetch (per-link override or global mode)
  if (prefetchForesight ?? prefetchMode === 'foresight') {
    return (
      <LinkForesight
        href={href}
        target={target}
        className={className}
        scroll={scroll}
        hitSlop={hitSlop}
        {...props}
      >
        {children}
      </LinkForesight>
    )
  }

  // 5. Viewport or off
  return (
    <NextLink
      className={className}
      target={target}
      href={href}
      scroll={scroll}
      prefetch={prefetchMode === 'viewport' ? undefined : false}
      onMouseEnter={onMouseEnter}
      {...props}
    >
      {children}
    </NextLink>
  )
}
